import { useEffect, useState } from 'react';
import {
  InAppContentDefinition,
  InAppDestination,
  InAppTriggerDefinition,
} from 'modules/graphql/types';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { FormField } from 'ui/formField';
import { Toggle } from 'ui/toggle';
import { CheckboxField, SettingsReadOnlyText } from '../../common';
import { getIsEditMode } from '../../common/utils';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useQueryParamContext } from '../../context/QueryParamContext';

export const ShowInInbox = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const inAppData = liveTemplate.mediumData as InAppContentDefinition;
  const [isToggleActive, setIsToggleActive] = useState<boolean>(inAppData.saveToInbox);

  const [isDeliverSilentlyChecked, setIsDeliverSilentlyChecked] = useState<boolean>(
    inAppData?.trigger === InAppTriggerDefinition.Immediate, // Default to unchecked.
  );
  /** If deliver silently is checked, then trigger: { type: Never }. */
  const isSaveInboxDataOn = inAppData.saveToInbox;
  const isDeliverSilentlyDataChecked = inAppData?.trigger === InAppTriggerDefinition.Never;

  /** If template targets only Roku don't show inbox toggle */
  const isRokuOnly =
    inAppData?.destinations?.includes(InAppDestination.Roku) &&
    inAppData?.destinations?.length === 1;
  /**
   * This function reads toggle and checkbox data, then reset state accordingly.
   * This is to handle unsaved changes when switching edit mode.
   * */
  const resetState = () => {
    /**
     * When toggle is on, checkbox isn't always checked. It can be checked or unchecked.
     * When toggle is off, checkbox is always unchecked, since we do not display checkbox if toggle is off.
     */
    // toggle is on.
    if (isSaveInboxDataOn) {
      setIsToggleActive(true);
    }
    // checkbox is checked.
    if (isDeliverSilentlyDataChecked) {
      setIsDeliverSilentlyChecked(true);
    }
    // if toggle is off, then turn off toggle AND checkbox.
    if (!isSaveInboxDataOn) {
      setIsToggleActive(false);
      setIsDeliverSilentlyChecked(false);
    }
    // if checkbox is unchecked, then uncheck checkbox only.
    if (!isDeliverSilentlyDataChecked) {
      setIsDeliverSilentlyChecked(false);
    }
  };

  useEffect(() => {
    resetState();
  }, [isEditModeVisible]);

  const handleSetToggle = () => {
    const newToggleStatus = !isToggleActive;
    setIsToggleActive(newToggleStatus);
    setCurrentMediumField('saveToInbox', newToggleStatus);
    /** If toggle is off, set 'trigger' to 'Immediate'. (meaning uncheck the checkbox). */
    if (!newToggleStatus) {
      setIsDeliverSilentlyChecked(false);
      setCurrentMediumField('trigger', InAppTriggerDefinition.Immediate);
    }
  };

  const handleCheckDeliverSilently = () => {
    const newDeliverSilentlyStatus = !isDeliverSilentlyChecked;
    setIsDeliverSilentlyChecked(newDeliverSilentlyStatus);
    setCurrentMediumField(
      'trigger',
      newDeliverSilentlyStatus ? InAppTriggerDefinition.Never : InAppTriggerDefinition.Immediate,
    );
  };

  return !isRokuOnly ? (
    <>
      <FormField isHorizontalLayout labelText={SETTINGS_DISPLAY_CONTENT.showInInbox.labelText}>
        {isEditModeVisible ? (
          <Toggle isActive={isToggleActive} onClick={handleSetToggle} />
        ) : (
          <SettingsReadOnlyText isUnset={!isSaveInboxDataOn} data-test="show-in-inbox-ro">
            {isSaveInboxDataOn ? 'On' : 'Off'}
          </SettingsReadOnlyText>
        )}
      </FormField>

      {isEditModeVisible && isToggleActive && (
        <FormField
          isHorizontalLayout
          labelText={SETTINGS_DISPLAY_CONTENT.deliverSilently.labelText}
        >
          <CheckboxField
            label={SETTINGS_DISPLAY_CONTENT.deliverSilently.checkboxLabel}
            status={isDeliverSilentlyChecked}
            onClick={handleCheckDeliverSilently}
          />
        </FormField>
      )}
      {!isEditModeVisible && isSaveInboxDataOn && (
        <FormField
          isHorizontalLayout
          labelText={SETTINGS_DISPLAY_CONTENT.deliverSilently.labelText}
        >
          <SettingsReadOnlyText isUnset={!isDeliverSilentlyDataChecked}>
            {isDeliverSilentlyDataChecked ? 'On' : 'Off'}
          </SettingsReadOnlyText>
        </FormField>
      )}
    </>
  ) : null;
};
